import { useState, useRef, useCallback, useEffect } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

const DEFAULT_MAX_SIZE = 200 * 1024 * 1024; // 200 MB

// Per-file status: pending → uploading → success | error
const FILE_STATUS = { PENDING: 'pending', UPLOADING: 'uploading', SUCCESS: 'success', ERROR: 'error' };

const StatusIcon = ({ status }) => {
  switch (status) {
    case FILE_STATUS.SUCCESS:  return <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />;
    case FILE_STATUS.ERROR:    return <ErrorIcon color="error" sx={{ fontSize: 20 }} />;
    case FILE_STATUS.UPLOADING: return <CircularProgress size={18} />;
    default:                   return <InsertDriveFileIcon color="disabled" sx={{ fontSize: 20 }} />;
  }
};

/**
 * Multi-file drag-and-drop upload modal.
 *
 * Props:
 *  - show            {boolean}
 *  - onClose         {function}
 *  - onConfirm       {function(file)}  single-file confirm mode
 *  - uploadFilesFn   {function(file, onProgress) => Promise}  upload a single file
 *  - uploadBatchFn   {function(files) => Promise}  upload all files in one request
 *  - onAllComplete   {function}
 *  - title           {string}
 *  - confirmLabel    {string}
 *  - accept          {string}
 *  - allowedTypes    {string[]|null}
 *  - maxSize         {number}
 *  - hint            {string}
 */
const FileUploadModal = ({
  show,
  onClose,
  onConfirm,
  uploadFilesFn,
  uploadBatchFn,
  onAllComplete,
  title = 'Upload Documents',
  confirmLabel = 'Upload All',
  accept = '.pdf,.png,.jpg,.jpeg',
  allowedTypes = null,
  multiple = true,
  maxSize = DEFAULT_MAX_SIZE,
  hint,
}) => {
  // Each entry: { id, file, status, progress, error }
  const [queue, setQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef(null);
  const idCounter = useRef(0);

  useEffect(() => {
    if (!show) {
      setQueue([]);
      setFileError(null);
      setIsUploading(false);
      idCounter.current = 0;
    }
  }, [show]);

  const validateFile = (f) => {
    if (allowedTypes && !allowedTypes.includes(f.type)) {
      return `"${f.name}" — unsupported file type (${f.type || 'unknown'}).`;
    }
    if (f.size > maxSize) {
      return `"${f.name}" exceeds the ${formatFileSize(maxSize)} limit.`;
    }
    return null;
  };

  const addFiles = useCallback((fileList) => {
    setFileError(null);
    const errors = [];
    const newEntries = [];
    for (const f of Array.from(fileList)) {
      const err = validateFile(f);
      if (err) { errors.push(err); continue; }
      newEntries.push({
        id: ++idCounter.current,
        file: f,
        status: FILE_STATUS.PENDING,
        progress: 0,
        error: null,
      });
    }
    if (errors.length) setFileError(errors.join(' '));
    if (newEntries.length) setQueue(prev => multiple ? [...prev, ...newEntries] : [newEntries[0]]);
  }, [allowedTypes, maxSize]);

  const removeFile = (id) => {
    setQueue(prev => prev.filter(e => e.id !== id));
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleFileInput = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleConfirm = () => {
    if (!onConfirm || queue.length === 0) return;
    onConfirm(queue[0].file);
  };

  const handleUploadAll = async () => {
    if (!uploadFilesFn && !uploadBatchFn) return;
    setIsUploading(true);
    setFileError(null);

    const pending = queue.filter(e => e.status === FILE_STATUS.PENDING);

    setQueue(prev => prev.map(e =>
      e.status === FILE_STATUS.PENDING ? { ...e, status: FILE_STATUS.UPLOADING, progress: 0 } : e
    ));

    if (uploadBatchFn) {
      try {
        await uploadBatchFn(pending.map(e => e.file));
        const pendingIds = new Set(pending.map(e => e.id));
        setQueue(prev => prev.map(e =>
          pendingIds.has(e.id) ? { ...e, status: FILE_STATUS.SUCCESS, progress: 100, error: null } : e
        ));
      } catch (err) {
        const msg = getAxiosErrorMessage(err, 'Upload failed.');
        const pendingIds = new Set(pending.map(e => e.id));
        setQueue(prev => prev.map(e =>
          pendingIds.has(e.id) ? { ...e, status: FILE_STATUS.ERROR, error: msg } : e
        ));
      }
    } else {
      const promises = pending.map(entry =>
        uploadFilesFn(entry.file, (progress) => {
          setQueue(prev => prev.map(e => e.id === entry.id ? { ...e, progress } : e));
        })
          .then(() => {
            setQueue(prev => prev.map(e => e.id === entry.id ? { ...e, status: FILE_STATUS.SUCCESS, progress: 100 } : e));
          })
          .catch((err) => {
            const msg = getAxiosErrorMessage(err, 'Upload failed.');
            setQueue(prev => prev.map(e => e.id === entry.id ? { ...e, status: FILE_STATUS.ERROR, error: msg } : e));
          })
      );

      await Promise.allSettled(promises);
    }

    setIsUploading(false);
    onAllComplete?.();
  };

  const pendingCount = queue.filter(e => e.status === FILE_STATUS.PENDING).length;
  const successCount = queue.filter(e => e.status === FILE_STATUS.SUCCESS).length;
  const errorCount = queue.filter(e => e.status === FILE_STATUS.ERROR).length;
  const allDone = queue.length > 0 && pendingCount === 0 && !isUploading;
  const defaultHint = `${accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')} — max ${formatFileSize(maxSize)}`;

  return (
    <Dialog open={show} onClose={isUploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span>{title}</span>
        {!isUploading && (
          <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {/* Drop zone */}
        <Box
          onClick={isUploading ? undefined : () => inputRef.current?.click()}
          onDrop={isUploading ? undefined : handleDrop}
          onDragOver={isUploading ? undefined : handleDragOver}
          onDragLeave={isUploading ? undefined : handleDragLeave}
          sx={{
            border: 2,
            borderStyle: dragOver ? 'solid' : 'dashed',
            borderColor: dragOver ? 'secondary.main' : 'divider',
            bgcolor: dragOver ? 'action.hover' : 'transparent',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            mb: 2,
            cursor: isUploading ? 'default' : 'pointer',
            transition: 'all 0.15s',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: dragOver ? 'secondary.main' : 'text.disabled' }} />
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
            {dragOver ? 'Drop files here' : 'Drag & drop files here'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            or click to browse{multiple ? ' — select multiple files' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {hint || defaultHint}
          </Typography>
          <Box component="input" ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={handleFileInput} sx={{ display: 'none' }} />
        </Box>

        {/* File queue */}
        {queue.length > 0 && (
          <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {queue.map((entry) => (
              <Stack key={entry.id} direction="row" alignItems="center" spacing={1} sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                <StatusIcon status={entry.status} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{entry.file.name}</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary">{formatFileSize(entry.file.size)}</Typography>
                    {entry.status === FILE_STATUS.UPLOADING && (
                      <LinearProgress variant="determinate" value={entry.progress} sx={{ flex: 1, height: 4, borderRadius: 2 }} />
                    )}
                    {entry.status === FILE_STATUS.SUCCESS && (
                      <Typography variant="caption" color="success.main">Uploaded</Typography>
                    )}
                    {entry.status === FILE_STATUS.ERROR && (
                      <Typography variant="caption" color="error.main" noWrap title={entry.error}>{entry.error}</Typography>
                    )}
                  </Stack>
                </Box>
                {entry.status === FILE_STATUS.PENDING && (
                  <IconButton size="small" color="error" onClick={() => removeFile(entry.id)} title="Remove">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Box>
        )}

        {fileError && <Alert severity="warning" sx={{ mt: 1 }}>{fileError}</Alert>}

        {allDone && (
          <Alert severity={errorCount > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
            {successCount} of {queue.length} file{queue.length !== 1 ? 's' : ''} uploaded successfully.
            {errorCount > 0 && ` ${errorCount} failed.`}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={isUploading} onClick={onClose}>
          {allDone ? 'Close' : 'Cancel'}
        </Button>
        {!allDone && (
          <Button
            variant="contained"
            disabled={queue.length === 0 || isUploading}
            onClick={onConfirm ? handleConfirm : handleUploadAll}
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
          >
            {isUploading
              ? `Uploading ${queue.filter(e => e.status === FILE_STATUS.UPLOADING).length} file${queue.filter(e => e.status === FILE_STATUS.UPLOADING).length !== 1 ? 's' : ''}…`
              : `${confirmLabel} (${queue.length})`
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadModal;
